import type { ProductCardProps } from "@/components/product";
import { ProductCard } from "@/components/product";
import { useCareplanContext } from "@/domains/careplan/contexts/use-careplan-context";
import { Divider, focusRing } from "@/ui";

export const CareplanProduct = ({ product, ref }: ProductCardProps) => {
  const careplan = useCareplanContext();

  const changes = careplan.getProductRoleChanges(product.role);

  return (
    /*
      Focusable from script only (tabIndex -1), so the summary card's "See Product & Plan Details"
      can move keyboard and screen reader users here; the label names the card when it is reached.
    */
    <ProductCard.Root
      aria-label={product.name}
      className={`flex scroll-mt-20 flex-col gap-6 overflow-hidden px-5 py-6 sm:gap-8 sm:px-10 sm:py-10 ${focusRing}`}
      key={product.id}
      product={{
        ...product,
        changes,
      }}
      ref={ref}
      tabIndex={-1}
    >
      <div className="flex flex-col">
        {/* Eyebrow row: "For Your ..." left, the update tag right. */}
        <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <ProductCard.WhyRecommended />
          <ProductCard.UpdatedAt className="sm:ml-auto sm:shrink-0" />
        </div>

        {/* Equal space above and below the hairline: the margin lives on the line itself. */}
        <Divider.Line className="my-3.5 sm:my-[18px]" />

        {/*
          Desktop rows are auto then 1fr: the carousel spans both, and the leftover height
          stays in the second row, so the description sits one gap under the price line.
        */}
        <div className="flex flex-col gap-6 md:grid md:grid-cols-2 md:grid-rows-[auto_1fr]">
          {/*
            Mobile: a two-column lockup, name and format left, price and list price right, the
            rating above and the cart link below. Desktop: one column beside the carousel, with
            the cart link on the price line.
          */}
          <div className="order-1 grid grid-cols-[minmax(0,1fr)_auto] gap-x-3.5 md:order-2 md:flex md:flex-col">
            {/* Rating to name: 8px on a phone, 12px on desktop. Name to format: 4px and 8px. */}
            <ProductCard.Rating className="col-span-2 mb-2 md:order-1 md:mb-3" />
            <ProductCard.Name className="min-w-0 text-[22px] leading-[1.15] md:order-2" level={2} />
            {/* Format to price line and price line to description are both one 24px gap. */}
            {/* Baseline-aligned so the link reads on the same line as the price digits. */}
            <div className="col-start-2 row-span-2 self-start md:order-4 md:mt-6 md:flex md:items-baseline md:justify-between md:gap-4">
              <ProductCard.CarePlanPrice />
              <ProductCard.CartLink className="hidden md:flex" compact />
            </div>
            <ProductCard.Format className="col-start-1 mt-1 md:order-3 md:mt-2" />
            <ProductCard.CartLink className="col-span-2 mt-2 md:hidden" />
          </div>
          <ProductCard.Carousel className="order-2 row-span-2 md:order-1" />
          <ProductCard.Description className="order-3 row-span-1 md:self-start" />
        </div>
      </div>
      <ProductCard.Instructions />
      <ProductCard.DosingAndEffects />
      <ProductCard.Details />
    </ProductCard.Root>
  );
};
